import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FormGroup, Grid, Typography } from '@mui/material'
import omit from 'lodash/omit'
import { CatalogProductType } from '../enums'
import { CatalogProduct } from '../types'
import CatalogProductForm, {
  CatalogProductSelectField,
  ElectricalCabinetFields,
  SpineConfigFields
} from './CatalogProductForm'
import { ElectricalCabinetCatalogProduct, SpineLoadCatalogProduct } from '../types/CatalogProduct'
import { SpineLoadCatalogProductCustomDataInput } from '../types/CatalogProductInput'

export interface SpineLoadCatalogProductFormProps {
  catalogProduct?: SpineLoadCatalogProduct
  onCancel?: () => void
  onError?: (error: unknown) => void
  afterSubmit?: (catalogProduct: CatalogProduct) => void
}

export default function SpineLoadCatalogProductForm({
  catalogProduct,
  ...props
}: SpineLoadCatalogProductFormProps) {
  return (
    <CatalogProductForm
      {...props}
      type={CatalogProductType.SPINE_LOAD}
      catalogProduct={catalogProduct}
    />
  )
}

export function SpineLoadCatalogProductCustomDataFields({
  value,
  disabled = false,
  onChange
}: {
  value: SpineLoadCatalogProductCustomDataInput
  catalogProduct?: CatalogProduct
  disabled?: boolean
  isError?: (field: string) => boolean
  getHelperText?: (field: string) => string | undefined
  onChange: (value: SpineLoadCatalogProductCustomDataInput) => void
}) {
  const { t } = useTranslation('catalogProduct')

  const [electricalCabinet, setElectricalCabinet] =
    useState<ElectricalCabinetCatalogProduct | null>(null)

  function handleGeneratorChange(selected: CatalogProduct | null) {
    // The loaded catalog product is already linked.
    if (selected !== null && selected.id === value.generator?.catalogProductId) {
      return
    }

    if (selected === null) {
      onChange(omit(value, 'generator'))
      return
    }

    onChange({ ...value, generator: { catalogProductId: selected.id } })
  }

  return (
    <Grid container spacing={6}>
      <Grid item xs={12}>
        <SpineConfigFields
          value={value}
          disabled={disabled}
          electricalCabinet={electricalCabinet}
          onChange={onChange}
        />
      </Grid>

      <Grid item xs={12}>
        <ElectricalCabinetFields
          value={value}
          disabled={disabled}
          onChange={onChange}
          onElectricalCabinetChange={setElectricalCabinet}
        />
      </Grid>

      <Grid item xs={12}>
        <FormGroup>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 2 }}>
                {t('generator')}
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <CatalogProductSelectField
                type={CatalogProductType.GENERATOR}
                value={value.generator?.catalogProductId ?? null}
                disabled={disabled}
                onChange={handleGeneratorChange}
              />
            </Grid>
          </Grid>
        </FormGroup>
      </Grid>
    </Grid>
  )
}
